/**
 * Suburb lookup — pure read API over `suburb_cache`.
 *
 * Never hits Google Sheets directly. The resync function keeps the cache fresh
 * every 6 hours (or on-demand from the settings page). These reads are fast
 * because the table has indexes on (binding_id, lower(suburb), state) and
 * (binding_id, postcode).
 */
import { getSupabase } from './supabase';

export interface SuburbRow {
  suburb: string;
  postcode: string | null;
  state: string | null;
  zone: string | null;
  verdictLabel: string | null;
  raw: Record<string, unknown>;
}

interface SuburbCacheRecord {
  suburb: string;
  postcode: string | null;
  state: string | null;
  zone: string | null;
  verdict_label: string | null;
  raw_row: Record<string, unknown>;
}

function toRow(r: SuburbCacheRecord): SuburbRow {
  return {
    suburb: r.suburb,
    postcode: r.postcode,
    state: r.state,
    zone: r.zone,
    verdictLabel: r.verdict_label,
    raw: r.raw_row ?? {},
  };
}

export async function getSuburbByName(
  name: string,
  state?: string,
): Promise<SuburbRow | null> {
  const supabase = getSupabase();
  let query = supabase
    .from('v9_suburb_cache')
    .select('suburb, postcode, state, zone, verdict_label, raw_row')
    .ilike('suburb', name.trim());

  if (state) query = query.eq('state', state.toUpperCase());

  const { data, error } = await query.limit(1).maybeSingle();
  if (error) {
    console.warn('[suburbs] getSuburbByName error:', error.message);
    return null;
  }
  return data ? toRow(data as SuburbCacheRecord) : null;
}

export async function getSuburbByPostcode(postcode: string): Promise<SuburbRow[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_suburb_cache')
    .select('suburb, postcode, state, zone, verdict_label, raw_row')
    .eq('postcode', postcode.trim());
  if (error) {
    console.warn('[suburbs] getSuburbByPostcode error:', error.message);
    return [];
  }
  return (data ?? []).map((r) => toRow(r as SuburbCacheRecord));
}

/**
 * Resolve a suburb to its verdict triple. The `meaning` / `action` / `theme`
 * fields are looked up via the `verdicts` purpose binding if one exists — for
 * now we synthesise sensible defaults from the label so callers always get
 * something printable.
 */
export async function getVerdictForSuburb(
  name: string,
  state?: string,
): Promise<{ label: string; meaning: string; action: string } | null> {
  const suburb = await getSuburbByName(name, state);
  if (!suburb?.verdictLabel) return null;

  const label = suburb.verdictLabel;
  return {
    label,
    meaning: defaultMeaningFor(label),
    action: defaultActionFor(label),
  };
}

// Sensible fallbacks until a Verdicts binding lands.
function defaultMeaningFor(label: string): string {
  const l = label.toLowerCase();
  if (l.includes('hot')) return 'High-density damage zone — prioritise routing.';
  if (l.includes('warm')) return 'Active damage — book within 48 hours.';
  if (l.includes('cool')) return 'Light damage — schedule alongside nearby jobs.';
  if (l.includes('cold')) return 'No verified damage — quote only on request.';
  return `Verdict: ${label}.`;
}

function defaultActionFor(label: string): string {
  const l = label.toLowerCase();
  if (l.includes('hot')) return 'Auto-route to senior inspector. Same-day callback.';
  if (l.includes('warm')) return 'Auto-route to next available inspector.';
  if (l.includes('cool')) return 'Queue for cluster routing.';
  if (l.includes('cold')) return 'Manual review — phone-quote only.';
  return 'Review case manually.';
}

/**
 * Normalise a suburb name for joining across data sources (the ABS geojson
 * `properties.name` vs the `v9_suburb_cache.suburb` column). Strips a trailing
 * "(Region)" disambiguator, lowercases, drops punctuation, collapses spaces —
 * so "Ascot (Brisbane - Qld)" and "ascot" both key to "ascot".
 */
export function normaliseSuburbName(name: string | null | undefined): string {
  return String(name || '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .toLowerCase()
    .replace(/[‘’']/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface SuburbZoneVerdict {
  zone: string | null;
  verdictLabel: string | null;
  state: string | null;
}

/**
 * Load every suburb's zone + verdict for the current tenant, keyed by
 * normalised suburb name. Used to colour the deck.gl suburb-polygon overlay
 * (join geojson polygons → this map). Paginated so it survives the Supabase
 * default 1000-row cap.
 */
export async function loadAllSuburbZones(): Promise<Map<string, SuburbZoneVerdict>> {
  const supabase = getSupabase();
  const out = new Map<string, SuburbZoneVerdict>();
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('v9_suburb_cache')
      .select('suburb, state, zone, verdict_label')
      .range(from, from + PAGE - 1);
    if (error) {
      console.warn('[suburbs] loadAllSuburbZones error:', error.message);
      break;
    }
    const rows = (data ?? []) as {
      suburb: string;
      state: string | null;
      zone: string | null;
      verdict_label: string | null;
    }[];
    for (const r of rows) {
      if (!r.suburb) continue;
      out.set(normaliseSuburbName(r.suburb), {
        zone: r.zone,
        verdictLabel: r.verdict_label,
        state: r.state,
      });
    }
    if (rows.length < PAGE) break;
  }
  return out;
}
